import React from 'react';
import { ChevronRight as ChevronRightIcon, ImportExport as SortIcon } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';

import type { ComplianceRuleDocument, LearningRuleDocument, ProviderRuleResponse } from '../types';

interface RuleTableRowProps {
  ruleResponse: ProviderRuleResponse;
  onClick: () => void;
}

export const RuleTableRow: React.FC<RuleTableRowProps> = ({ ruleResponse, onClick }) => {
  const theme = useTheme();
  const { rule, rule_type, is_global, is_disabled } = ruleResponse;

  // Extract display values based on rule type
  const title = rule_type === 'compliance' 
    ? (rule as ComplianceRuleDocument).title || (rule as ComplianceRuleDocument).selector_text || 'Untitled Rule'
    : (rule as LearningRuleDocument).title || 'Untitled Rule';
  
  // Extract CPT codes and payers from facets (only for compliance rules)
  const cptCodes = rule_type === 'compliance' 
    ? (rule as ComplianceRuleDocument).facets?.cpt || []
    : [];
  const payers = rule_type === 'compliance'
    ? (rule as ComplianceRuleDocument).facets?.payer || []
    : [];
  
  // Show first 3 CPT codes, with "+N more" if there are more
  const visibleCptCodes = cptCodes.slice(0, 3);
  const remainingCptCount = cptCodes.length - 3;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1fr 48px',
        alignItems: 'center',
        px: 2,
        py: 2,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: 'pointer',
        opacity: is_disabled ? 0.6 : 1,
        '&:hover': {
          bgcolor: '#FBFBFF',
        },
      }}
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <Checkbox size="small" />
      </Box>

      {/* Rule Title with Scope and Type as subtitle */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
          {is_disabled && (
            <Chip
              label="Disabled"
              variant="outlined"
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                borderColor: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.6875rem',
                height: 18,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          {!is_disabled && (
            <Chip
              label={is_global ? 'Global' : 'Organization'}
              variant="outlined"
              size="small"
              sx={{
                color: is_global ? '#4CAF50' : theme.palette.primary.main,
                borderColor: is_global ? '#4CAF50' : theme.palette.primary.main,
                fontWeight: 500,
                fontSize: '0.6875rem',
                height: 18,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          <Chip
            label={rule_type === 'compliance' ? 'Compliance' : 'Learning'}
            variant="outlined"
            size="small"
            sx={{
              color: rule_type === 'compliance' ? theme.palette.warning.main : theme.palette.info.main,
              borderColor: rule_type === 'compliance' ? theme.palette.warning.main : theme.palette.info.main,
              fontWeight: 500,
              fontSize: '0.6875rem',
              height: 18,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>
      </Box>

      {/* CPT Codes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {visibleCptCodes.length > 0 ? (
          <>
            {visibleCptCodes.map((cpt) => (
              <Chip
                key={cpt}
                label={`CPT ${cpt}`}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.6875rem',
                  height: 20,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ))}
            {remainingCptCount > 0 && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 0.5 }}>
                +{remainingCptCount} more
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            —
          </Typography>
        )}
      </Box>

      {/* Payers */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {payers.length > 0 ? (
          payers.map((payer) => (
            <Chip
              key={payer}
              label={payer}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.6875rem',
                height: 20,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ))
        ) : (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            —
          </Typography>
        )}
      </Box>

      <Box>
        <ChevronRightIcon sx={{ color: theme.palette.grey[400] }} />
      </Box>
    </Box>
  );
};

export const RuleTableHeader: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1fr 48px',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        bgcolor: '#F9FAFB',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '12px 12px 0 0',
      }}
    >
      <Box>
        <Checkbox size="small" />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
          Rule
        </Typography>
        <SortIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
          CPT Codes
        </Typography>
        <SortIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
          Payers
        </Typography>
        <SortIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
      </Box>

      <Box />
    </Box>
  );
};

